import { type ReactNode, useMemo } from "react";

/**
 * Минимальный рендер Markdown для тел юридических документов.
 *
 * Живёт в ките, а не в витрине: тот же текст читают три поверхности —
 * модалка принятия в `id-web`, публичная читалка в `sso-web` и баннер cookie.
 * Вторая копия парсера означала бы, что документ выглядит по-разному в
 * зависимости от того, откуда его открыли.
 *
 * Своя реализация, а не библиотека: в бандл витрины не хочется тащить парсер с
 * плагинами ради шести конструкций, а `dangerouslySetInnerHTML` на тексте,
 * который редактирует Legal Center, — это XSS на самой чувствительной
 * странице. Здесь HTML не собирается вовсе: разметка сразу становится
 * React-узлами, поэтому вставить в документ тег физически нечем.
 *
 * Поддержано ровно то, чем пишут документы: заголовки, абзацы, списки,
 * таблицы GFM, цитаты, разделители и строчные `**жирный**`, `*курсив*`,
 * `` `код` ``, `[ссылка](url)`. Жирного начертания в системе нет — акцент даётся цветом.
 */
export interface MarkdownProps {
	/** Тело документа в Markdown. HTML внутри не интерпретируется. */
	source: string;
	className?: string;
}

export function Markdown({ source, className }: MarkdownProps) {
	const blocks = useMemo(() => parseBlocks(source), [source]);
	return <div className={className}>{blocks}</div>;
}

type Block =
	| { kind: "heading"; level: number; text: string }
	| { kind: "paragraph"; text: string }
	| { kind: "quote"; text: string }
	| { kind: "list"; ordered: boolean; items: string[] }
	| { kind: "table"; head: string[]; rows: string[][] }
	| { kind: "rule" };

const HEADING = /^(#{1,6})\s+(.*)$/;
const UNORDERED = /^[-*]\s+(.*)$/;
const ORDERED = /^\d+[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^(-{3,}|\*{3,}|_{3,})$/;
/** Строка таблицы GFM: `| a | b |`. */
const TABLE_ROW = /^\|(.+)\|$/;
/** Разделитель шапки: `|---|:--:|`. Он и отличает таблицу от строки с трубами. */
const TABLE_DIVIDER = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

/** Ячейки строки таблицы без обрамляющих труб. */
function tableCells(line: string): string[] {
	const inner = TABLE_ROW.exec(line)?.[1] ?? line;
	return inner.split("|").map((cell) => cell.trim());
}

/**
 * «Ленивое продолжение» пункта списка: строка, перенесённая в исходнике, — часть
 * ЭТОГО пункта, а не новый абзац.
 *
 * Без этого длинный пункт, разбитый по 80 символов (а юридические документы
 * пишут именно так), вываливался из списка и печатался абзацем без маркера и
 * без отступа.
 *
 * Вынесено из `parseBlocks` не ради красоты: с этой веткой внутри функция
 * перевалила за потолок когнитивной сложности, а он движется только вниз
 * (rules/code-quality §30-32).
 */
function continueListItem(
	list: { items: string[] } | null,
	line: string,
	hasPendingBlock: boolean,
): boolean {
	if (!list || hasPendingBlock) return false;
	const last = list.items.length - 1;
	if (last < 0) return false;
	list.items[last] = `${list.items[last]} ${line}`;
	return true;
}

/**
 * Таблица GFM, начинающаяся с этой строки, — либо `null`.
 *
 * Признак таблицы не сама строка труб, а разделитель шапки под ней: в
 * юридическом тексте вертикальная черта встречается и в обычном предложении,
 * и превращать такой абзац в таблицу нельзя.
 */
function tableAt(
	lines: readonly string[],
	index: number,
): { block: Block; nextIndex: number } | null {
	const head = (lines[index] ?? "").trim();
	const divider = (lines[index + 1] ?? "").trim();
	if (!TABLE_ROW.test(head) || !TABLE_DIVIDER.test(divider)) return null;

	const rows: string[][] = [];
	let cursor = index + 2;
	while (cursor < lines.length) {
		const line = (lines[cursor] ?? "").trim();
		if (!TABLE_ROW.test(line)) break;
		rows.push(tableCells(line));
		cursor += 1;
	}

	return { block: { kind: "table", head: tableCells(head), rows }, nextIndex: cursor };
}

/** Блок из одной строки — заголовок или разделитель. `null`, если строка иная. */
function standaloneBlock(line: string): Block | null {
	const heading = HEADING.exec(line);
	if (heading?.[1] && heading[2] !== undefined) {
		return { kind: "heading", level: heading[1].length, text: heading[2] };
	}
	return RULE.test(line) ? { kind: "rule" } : null;
}

/** Пункт списка: маркер или номер. `null` — строка не пункт. */
function listItemAt(line: string): { ordered: boolean; item: string } | null {
	const unordered = UNORDERED.exec(line);
	if (unordered?.[1] !== undefined) return { ordered: false, item: unordered[1] };
	const ordered = ORDERED.exec(line);
	if (ordered?.[1] !== undefined) return { ordered: true, item: ordered[1] };
	return null;
}

/** Разбор на блоки: строки одного вида склеиваются, пустая строка закрывает блок. */
function parseBlocks(source: string): ReactNode[] {
	const lines = source.replaceAll("\r\n", "\n").split("\n");
	const blocks: Block[] = [];
	let paragraph: string[] = [];
	let quote: string[] = [];
	let list: { ordered: boolean; items: string[] } | null = null;

	const flush = () => {
		if (paragraph.length > 0) {
			blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
			paragraph = [];
		}
		if (quote.length > 0) {
			blocks.push({ kind: "quote", text: quote.join(" ") });
			quote = [];
		}
		if (list) {
			blocks.push({ kind: "list", ordered: list.ordered, items: list.items });
			list = null;
		}
	};

	for (let index = 0; index < lines.length; index += 1) {
		const line = (lines[index] ?? "").trim();
		if (line.length === 0) {
			flush();
			continue;
		}

		// Таблица целиком забирается отдельной функцией: внутри цикла её разбор
		// стоил потолка когнитивной сложности (rules/code-quality §30-32).
		const table = tableAt(lines, index);
		if (table) {
			flush();
			blocks.push(table.block);
			index = table.nextIndex - 1;
			continue;
		}

		// Заголовок и разделитель — блоки из одной строки, без состояния.
		const standalone = standaloneBlock(line);
		if (standalone) {
			flush();
			blocks.push(standalone);
			continue;
		}

		const quoted = QUOTE.exec(line);
		if (quoted) {
			if (paragraph.length > 0 || list) flush();
			quote.push(quoted[1] ?? "");
			continue;
		}

		const listItem = listItemAt(line);
		if (listItem) {
			const { ordered: isOrdered, item } = listItem;
			if (paragraph.length > 0 || quote.length > 0) flush();
			if (list && list.ordered !== isOrdered) flush();
			list ??= { ordered: isOrdered, items: [] };
			list.items.push(item);
			continue;
		}

		if (continueListItem(list, line, paragraph.length > 0 || quote.length > 0)) continue;

		if (quote.length > 0 || list) flush();
		paragraph.push(line);
	}
	flush();

	return blocks.map((block, index) => renderBlock(block, index));
}

function renderBlock(block: Block, key: number): ReactNode {
	switch (block.kind) {
		case "heading": {
			// Иерархия из размера и цвета: начертание везде обычное. Но тег —
			// настоящий заголовок, а не <p>: у юридического документа структуру
			// читает скринридер по h2/h3/h4, и «сплошной текст абзацами» лишает
			// его оглавления (аудит В6).
			//
			// Смещение на уровень: h1 страницы принадлежит разделу витрины, тело
			// документа начинается с h2. Глубже h4 не спускаемся — визуальных
			// ролей всё равно две, а h5/h6 в документах не встречаются.
			const Tag: "h2" | "h3" | "h4" = block.level <= 1 ? "h2" : block.level === 2 ? "h3" : "h4";
			const className = block.level <= 2 ? "t-subsection mt-4 first:mt-0" : "t-row mt-3 first:mt-0";
			return (
				<Tag key={key} className={className}>
					{inline(block.text)}
				</Tag>
			);
		}
		case "quote":
			return (
				// Полоса нейтральная: зелёный в системе означает действие и
				// идентичность, декоративной полосой цитаты он быть не может
				// (бренд-бриф «never as decoration», аудит В6).
				<blockquote
					key={key}
					className="my-3 border-hairline border-l-2 pl-3 text-fg-secondary italic"
				>
					{inline(block.text)}
				</blockquote>
			);
		case "list": {
			const ListTag = block.ordered ? "ol" : "ul";
			return (
				<ListTag key={key} className="my-2 flex list-outside flex-col gap-1 pl-5 text-fg-secondary">
					{block.items.map((item, itemIndex) => (
						<li key={itemIndex} className={block.ordered ? "list-decimal" : "list-disc"}>
							{inline(item)}
						</li>
					))}
				</ListTag>
			);
		}
		case "table":
			return (
				// Таблица шире колонки — обычное дело (цены на четыре тарифа),
				// поэтому она скроллится внутри своего контейнера, а не растягивает
				// страницу вбок.
				<div key={key} className="iec-scroll my-3 overflow-x-auto">
					<table className="w-full border-collapse text-left text-fg-secondary">
						<thead>
							<tr>
								{block.head.map((cell, cellIndex) => (
									<th
										key={cellIndex}
										className="border-hairline border-b px-2 py-1.5 font-normal text-fg"
									>
										{inline(cell)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{block.rows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{row.map((cell, cellIndex) => (
										<td key={cellIndex} className="border-hairline border-b px-2 py-1.5">
											{inline(cell)}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			);
		case "rule":
			return <hr key={key} className="my-4 border-hairline border-t" />;
		default:
			return (
				<p key={key} className="mt-2 text-fg-secondary first:mt-0">
					{inline(block.text)}
				</p>
			);
	}
}

const INLINE =
	/(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)|(`[^`]+`)|(\[[^\]]+\]\((?:https?:\/\/|mailto:)[^)\s]+\))/g;

/** Строчная разметка. Ссылки принимаются только http(s) и mailto — `javascript:` не пройдёт. */
function inline(text: string): ReactNode[] {
	const nodes: ReactNode[] = [];
	let cursor = 0;
	let key = 0;

	for (const match of text.matchAll(INLINE)) {
		const token = match[0];
		const start = match.index;
		if (start > cursor) nodes.push(text.slice(cursor, start));
		cursor = start + token.length;

		if (token.startsWith("**")) {
			nodes.push(
				<strong key={key++} className="font-normal text-fg">
					{token.slice(2, -2)}
				</strong>,
			);
		} else if (token.startsWith("`")) {
			nodes.push(
				<code key={key++} className="rounded-chip bg-sunken px-1 font-mono text-[0.95em]">
					{token.slice(1, -1)}
				</code>,
			);
		} else if (token.startsWith("[")) {
			const split = token.indexOf("](");
			const label = token.slice(1, split);
			const href = token.slice(split + 2, -1);
			nodes.push(
				<a key={key++} href={href} target="_blank" rel="noreferrer noopener" className="underline">
					{label}
				</a>,
			);
		} else {
			nodes.push(
				<em key={key++} className="italic">
					{token.slice(1, -1)}
				</em>,
			);
		}
	}

	if (cursor < text.length) nodes.push(text.slice(cursor));
	return nodes;
}
